import { IStringerGeometry, IVec3 } from '@stair-craft/common/util';

import { STRINGER_THICKNESS, STRINGER_WIDTH, TETIVA_TOP_MARGIN } from '../../constants';

/** One тетива of a flight, described along the walking direction. */
export interface ITetivaBoardInput {
    readonly side: 'left' | 'right';
    /** Plan point of the flight's first riser line on this edge, and the walking direction (unit). */
    readonly origin: { x: number; z: number };
    readonly direction: { x: number; z: number };
    /** Tread top of the flight's first step. */
    readonly firstTop: number;
    readonly riserHeight: number;
    readonly treadDepth: number;
    readonly noseLength: number;
    /** Board ends along the walking direction, from the first riser line. */
    readonly from: number;
    readonly to: number;
    /** Upper floor surface: a board that reaches it is cut flush. */
    readonly maxY: number;
}

/** Lift of the board top above the riser-line corners: clears the nosing by TETIVA_TOP_MARGIN. */
export function tetivaLift(riserHeight: number, treadDepth: number, noseLength: number): number {
    return TETIVA_TOP_MARGIN + (riserHeight / treadDepth) * noseLength;
}

/** Vertical depth of a тетива board on a flight of this slope. */
export function tetivaDrop(riserHeight: number, treadDepth: number): number {
    return STRINGER_WIDTH / Math.cos(Math.atan2(riserHeight, treadDepth));
}

/**
 * A тетива as a side profile: the top runs parallel to the nosing line, `tetivaLift` above the
 * riser-line corners, and is cut flush at the upper floor; the bottom is the same line
 * `tetivaDrop` lower. Both ends are vertical cuts; the renderer trims the board at the floor.
 */
export function tetivaBoard(input: ITetivaBoardInput): IStringerGeometry {
    const slope: number = input.riserHeight / input.treadDepth;
    const lift: number = tetivaLift(input.riserHeight, input.treadDepth, input.noseLength);
    const drop: number = tetivaDrop(input.riserHeight, input.treadDepth);
    const topAt: (u: number) => number = (u: number): number => input.firstTop + lift + slope * u;
    const at: (u: number, y: number) => IVec3 = (u: number, y: number): IVec3 => ({
        y,
        x: input.origin.x + input.direction.x * u,
        z: input.origin.z + input.direction.z * u,
    });
    const top: IVec3[] = [at(input.from, Math.min(topAt(input.from), input.maxY))];
    if (topAt(input.to) > input.maxY && topAt(input.from) < input.maxY) {
        const reach: number = (input.maxY - input.firstTop - lift) / slope;
        top.push(at(reach, input.maxY));
    }
    top.push(at(input.to, Math.min(topAt(input.to), input.maxY)));
    return {
        side: input.side,
        points: top,
        bottom: [at(input.from, topAt(input.from) - drop), at(input.to, topAt(input.to) - drop)],
        thickness: STRINGER_THICKNESS,
        width: STRINGER_WIDTH,
    };
}
